import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
} from 'react';
import { Banner, Button, Dialog } from '../../../ds/components';
import type { AvatarCrop } from '../../onboarding/user/types';
import styles from './AvatarCropDialog.module.css';

/**
 * Einstellungen 02 (shared with Onboarding Nutzer 02/03): the circular 200-px mask over the 260-px image area,
 * drag to move, the zoom slider, file name · size, „Abbrechen"/„Übernehmen". The crop is returned in source pixels
 * (`x`, `y`, `size`) — the server stores it and the client renders it (B2: avatars are not re-encoded).
 */

export const MASK = 200;
const STAGE_HEIGHT = 260;
const MAX_ZOOM = 3;

export interface CropSource {
  /** Object URL or server URL of the image. */
  url: string;
  name: string;
  /** Bytes; null for an already stored image. */
  bytes: number | null;
}

export interface AvatarCropDialogProps {
  open: boolean;
  source: CropSource | null;
  initialCrop?: AvatarCrop | null | undefined;
  busy?: boolean | undefined;
  error?: string | null | undefined;
  onClose: () => void;
  onApply: (crop: AvatarCrop) => void;
}

/** „2,1 MB" / „640 KB". */
export function fileSizeLabel(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
  return `${Math.max(1, Math.round(bytes / 1024))} KB`;
}

interface Natural {
  width: number;
  height: number;
}

interface View {
  zoom: number;
  /** Image top-left relative to the mask's top-left, in screen pixels. */
  x: number;
  y: number;
}

function baseScale(natural: Natural): number {
  return MASK / Math.min(natural.width, natural.height);
}

function clampView(view: View, natural: Natural): View {
  const scale = baseScale(natural) * view.zoom;
  const width = natural.width * scale;
  const height = natural.height * scale;
  return {
    zoom: view.zoom,
    x: Math.min(0, Math.max(MASK - width, view.x)),
    y: Math.min(0, Math.max(MASK - height, view.y)),
  };
}

function viewFromCrop(crop: AvatarCrop | null | undefined, natural: Natural): View {
  if (!crop || crop.size <= 0) {
    const scale = baseScale(natural);
    return { zoom: 1, x: (MASK - natural.width * scale) / 2, y: (MASK - natural.height * scale) / 2 };
  }
  const zoom = Math.min(MAX_ZOOM, Math.max(1, Math.min(natural.width, natural.height) / crop.size));
  const scale = baseScale(natural) * zoom;
  return clampView({ zoom, x: -crop.x * scale, y: -crop.y * scale }, natural);
}

export function cropOf(view: View, natural: Natural): AvatarCrop {
  const scale = baseScale(natural) * view.zoom;
  const size = Math.min(natural.width, natural.height, Math.round(MASK / scale));
  const x = Math.min(natural.width - size, Math.max(0, Math.round(-view.x / scale)));
  const y = Math.min(natural.height - size, Math.max(0, Math.round(-view.y / scale)));
  return { x, y, size };
}

export function AvatarCropDialog({
  open,
  source,
  initialCrop,
  busy,
  error,
  onClose,
  onApply,
}: AvatarCropDialogProps) {
  const [natural, setNatural] = useState<Natural | null>(null);
  const [view, setView] = useState<View>({ zoom: 1, x: 0, y: 0 });
  const drag = useRef<{ pointer: number; startX: number; startY: number; view: View } | null>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const [stageWidth, setStageWidth] = useState(504);

  useEffect(() => {
    setNatural(null);
  }, [source?.url]);

  useEffect(() => {
    const element = stageRef.current;
    if (!element || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width;
      if (width) setStageWidth(width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [open]);

  const scale = natural ? baseScale(natural) * view.zoom : 1;
  const maskLeft = (stageWidth - MASK) / 2;
  const maskTop = (STAGE_HEIGHT - MASK) / 2;
  const zoomPercent = Math.round(((view.zoom - 1) / (MAX_ZOOM - 1)) * 100);
  const meta = useMemo(() => {
    if (!source) return '';
    return source.bytes !== null ? `${source.name} · ${fileSizeLabel(source.bytes)}` : source.name;
  }, [source]);

  function onLoad(image: HTMLImageElement) {
    const next = { width: image.naturalWidth, height: image.naturalHeight };
    setNatural(next);
    setView(viewFromCrop(initialCrop, next));
  }

  function setZoom(zoom: number) {
    if (!natural) return;
    // Zoom around the mask's centre.
    const before = baseScale(natural) * view.zoom;
    const after = baseScale(natural) * zoom;
    const centre = MASK / 2;
    const x = centre - ((centre - view.x) / before) * after;
    const y = centre - ((centre - view.y) / before) * after;
    setView(clampView({ zoom, x, y }, natural));
  }

  function onPointerDown(event: ReactPointerEvent<HTMLDivElement>) {
    if (!natural) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    drag.current = { pointer: event.pointerId, startX: event.clientX, startY: event.clientY, view };
  }

  function onPointerMove(event: ReactPointerEvent<HTMLDivElement>) {
    const state = drag.current;
    if (!state || state.pointer !== event.pointerId || !natural) return;
    setView(
      clampView(
        {
          zoom: state.view.zoom,
          x: state.view.x + event.clientX - state.startX,
          y: state.view.y + event.clientY - state.startY,
        },
        natural,
      ),
    );
  }

  function onPointerUp(event: ReactPointerEvent<HTMLDivElement>) {
    if (drag.current?.pointer === event.pointerId) drag.current = null;
  }

  function onKeyDown(key: string) {
    if (!natural) return;
    const step = 10;
    const delta: Record<string, [number, number]> = {
      ArrowLeft: [step, 0],
      ArrowRight: [-step, 0],
      ArrowUp: [0, step],
      ArrowDown: [0, -step],
    };
    const move = delta[key];
    if (!move) return;
    setView(clampView({ zoom: view.zoom, x: view.x + move[0], y: view.y + move[1] }, natural));
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Profilbild"
      title="Ausschnitt festlegen"
      width={560}
      className={styles.dialog}
      bodyClassName={styles.body}
      footerNote={meta}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Abbrechen
          </Button>
          <Button
            loading={busy}
            disabled={!natural}
            onClick={() => {
              if (natural) onApply(cropOf(view, natural));
            }}
          >
            Übernehmen
          </Button>
        </>
      }
    >
      <div
        ref={stageRef}
        className={styles.stage}
        role="img"
        aria-label="Ausschnitt des Profilbilds — mit der Maus oder den Pfeiltasten verschieben"
        tabIndex={0}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={(event) => onKeyDown(event.key)}
      >
        {source ? (
          <img
            src={source.url}
            alt=""
            draggable={false}
            className={styles.image}
            onLoad={(event) => onLoad(event.currentTarget)}
            style={
              natural
                ? {
                    width: natural.width * scale,
                    height: natural.height * scale,
                    left: maskLeft + view.x,
                    top: maskTop + view.y,
                  }
                : { visibility: 'hidden' }
            }
          />
        ) : null}
        <div className={styles.maskLayer} aria-hidden="true">
          <div className={styles.mask} />
        </div>
      </div>
      <label className={styles.zoomRow}>
        <span className={styles.zoomLabel}>Zoom</span>
        <input
          type="range"
          min={1}
          max={MAX_ZOOM}
          step={0.01}
          value={view.zoom}
          disabled={!natural}
          className={styles.zoom}
          style={{ '--zoom-fill': `${zoomPercent}%` } as CSSProperties}
          onChange={(event) => setZoom(Number(event.target.value))}
        />
      </label>
      {error ? <Banner tone="danger">{error}</Banner> : null}
    </Dialog>
  );
}
